/**
 * game-review.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * AI Game Review & Match Analysis engine for Xiangqi / Jieqi Web Client.
 *
 * Computes:
 *   - Fast single-pass evaluation (via WASM core if available, or heuristic fallback)
 *   - Move accuracy % (Win-probability based formula)
 *   - Badges: Brilliant (!!), Best (★), Excellent (✓), Good (•), Inaccuracy (?!), Mistake (?), Blunder (??)
 *   - Natural Xiangqi advantage descriptions (replacing raw cp)
 *   - Tactical/Strategic breakdown: Why wrong, Best alternative, Correct strategy
 *   - Single Biggest Blunder per player and Critical Turning Point
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type {
  MatchReplayData,
  GameReviewReport,
  MoveReview,
  MoveBadge,
  AlternativeMove,
  PlayerPerformance,
  KeyBlunderInfo,
  EvalPoint,
  Side,
} from '@xiangqi/protocol';
import type { XiangqiCore } from '@xiangqi/game-core';

export type {
  GameReviewReport,
  MoveReview,
  MoveBadge,
  AlternativeMove,
  PlayerPerformance,
  KeyBlunderInfo,
  EvalPoint,
  Side,
};

export type PieceKind =
  | 'king'
  | 'chariot'
  | 'cannon'
  | 'horse'
  | 'elephant'
  | 'advisor'
  | 'soldier';

export const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i'] as const;

export const KIND_VI_ABBR: Record<PieceKind, string> = {
  king: 'Tg',
  chariot: 'X',
  cannon: 'P',
  horse: 'M',
  elephant: 'V',
  advisor: 'S',
  soldier: 'B',
};

export const KIND_VI_NAME: Record<PieceKind, string> = {
  king: 'Tướng',
  chariot: 'Xe',
  cannon: 'Pháo',
  horse: 'Mã',
  elephant: 'Tượng',
  advisor: 'Sĩ',
  soldier: 'Tốt',
};

export function sq(rank: number, file: number): number {
  return rank * 9 + file;
}

export function getRank(square: number): number {
  return Math.floor(square / 9);
}

export function getFile(square: number): number {
  return square % 9;
}

export function getFileCol(square: number, side: Side): number {
  const f = getFile(square);
  return side === 'red' ? 9 - f : f + 1;
}

export function getInitialSquareRole(square: number): PieceKind {
  const r = getRank(square);
  const f = getFile(square);
  if (r === 0 || r === 9) {
    if (f === 0 || f === 8) return 'chariot';
    if (f === 1 || f === 7) return 'horse';
    if (f === 2 || f === 6) return 'elephant';
    if (f === 3 || f === 5) return 'advisor';
    if (f === 4) return 'king';
  }
  if ((r === 2 || r === 7) && (f === 1 || f === 7)) return 'cannon';
  if ((r === 3 || r === 6) && f % 2 === 0) return 'soldier';
  return 'soldier';
}

/** Chuyển đổi centipawns sang xác suất thắng (0..1) */
export function cpToWinRate(cp: number): number {
  return 1 / (1 + Math.pow(10, -cp / 1500));
}

/** Tính độ chính xác của 1 nước đi dựa trên mức giảm xác suất thắng */
export function calculateMoveAccuracy(winRateLoss: number): number {
  const loss = Math.max(0, winRateLoss);
  const acc = 100 * Math.exp(-3.5 * loss);
  return Math.max(0, Math.min(100, Math.round(acc * 10) / 10));
}

/** Phân loại huy hiệu nước đi */
export function classifyMoveBadge(
  winRateLoss: number,
  evalDeltaCp: number,
  isForced: boolean,
  isBrilliantCandidate: boolean,
): MoveBadge {
  if (isForced) return 'forced';
  if (isBrilliantCandidate && evalDeltaCp >= 2500) return 'brilliant';

  if (winRateLoss >= 0.22 || evalDeltaCp <= -3500) return 'blunder';
  if (winRateLoss >= 0.11 || evalDeltaCp <= -1800) return 'mistake';
  if (winRateLoss >= 0.05 || evalDeltaCp <= -800) return 'inaccuracy';
  if (winRateLoss >= 0.02 || evalDeltaCp <= -300) return 'good';
  if (winRateLoss >= 0.005) return 'excellent';
  return 'best';
}

/** Ước lượng Elo dựa trên Accuracy */
export function estimateRating(accuracy: number): number {
  if (accuracy >= 92) return 2300 + Math.round((accuracy - 92) * 50);
  if (accuracy >= 85) return 2000 + Math.round((accuracy - 85) * 42);
  if (accuracy >= 75) return 1700 + Math.round((accuracy - 75) * 30);
  if (accuracy >= 65) return 1400 + Math.round((accuracy - 65) * 30);
  if (accuracy >= 50) return 1100 + Math.round((accuracy - 50) * 20);
  return Math.max(800, Math.round(accuracy * 20));
}

/** Mô tả thế trận bằng ngôn ngữ tự nhiên */
export function describeAdvantage(evalCp: number): string {
  if (evalCp >= 3500) return 'Ưu thế áp đảo hoàn toàn (hơn tương đương 1 Xe)';
  if (evalCp >= 2000) return 'Chiếm ưu thế lớn (hơn tương đương Pháo/Mã)';
  if (evalCp >= 700) return 'Chiếm ưu thế chủ động (hơn Tốt qua sông / lộ đẹp)';
  if (evalCp >= -700) return 'Thế trận giằng co cân bằng';
  if (evalCp >= -2000) return 'Bị đối phương chiếm ưu thế';
  if (evalCp >= -3500) return 'Bị thất thế nặng nề (kém tương đương Pháo/Mã)';
  return 'Thế cờ hiểm nghèo / Nguy cơ bại trận';
}

/**
 * Sinh phân tích chiến lược đúng / sai chi tiết cho từng nước cờ
 */
export function generateStrategicDetails(
  badge: MoveBadge,
  side: Side,
  notationVi: string,
  pieceKind: PieceKind,
  evalDeltaCp: number,
  revealedKind?: string,
  capturedPiece?: string,
  isCheck?: boolean,
  bestAltVi?: string,
): {
  aiCommentary: string;
  strategicTag: string;
  whyWrong?: string;
  correctStrategy?: string;
} {
  const sideName = side === 'red' ? 'Đỏ' : 'Đen';
  const oppName = side === 'red' ? 'Đen' : 'Đỏ';
  const pieceName = KIND_VI_NAME[pieceKind] || 'Quân cờ';

  let strategicTag = 'Duy trì thế trận';
  if (badge === 'brilliant') {
    strategicTag = 'Đòn phối hợp xuất thần';
  } else if (badge === 'best') {
    strategicTag = 'Nước đi tối ưu nhất';
  } else if (badge === 'blunder') {
    if (isCheck) strategicTag = 'Đỡ chiếu hở sườn';
    else if (capturedPiece) strategicTag = 'Ăn quân rơi bẫy';
    else if (evalDeltaCp <= -3000) strategicTag = 'Mất quân chủ lực';
    else strategicTag = 'Sơ hở chiến thuật chí mạng';
  } else if (badge === 'mistake') {
    if (evalDeltaCp <= -1500) strategicTag = 'Mất nhịp - Mất quân';
    else strategicTag = 'Chậm nhịp tấn công';
  } else if (badge === 'inaccuracy') {
    strategicTag = 'Nước cờ chưa tối ưu';
  } else if (isCheck) {
    strategicTag = 'Chiếu công kích dồn dập';
  } else if (capturedPiece) {
    strategicTag = 'Ăn quân củng cố lực lượng';
  } else if (revealedKind) {
    strategicTag = 'Mở nắp cờ úp';
  }

  let whyWrong: string | undefined;
  if (badge === 'blunder' || badge === 'mistake' || badge === 'inaccuracy') {
    if (isCheck) {
      whyWrong = `Đỡ chiếu vội vàng hoặc chọn sai hướng phòng ngự, làm hở trung lộ và tạo thời cơ cho ${oppName} tiếp tục vây hãm.`;
    } else if (capturedPiece) {
      whyWrong = `Quá ham ăn ${capturedPiece} của đối phương mà bỏ quên phòng tuyến quan trọng, dính đòn phản công bất ngờ.`;
    } else if (evalDeltaCp <= -3000) {
      whyWrong = `Di chuyển ${pieceName} (${notationVi}) làm mất căn bảo vệ hoặc đi vào tầm ngắm, dẫn đến mất trắng quân chủ lực.`;
    } else if (evalDeltaCp <= -1500) {
      whyWrong = `Nước đi ${notationVi} làm chậm nhịp phối hợp giữa Xe - Pháo - Mã, để ${oppName} khai thác chiếm lĩnh lộ trọng yếu.`;
    } else {
      whyWrong = `Phương án ${notationVi} chưa đủ tích cực, bỏ lỡ cơ hội gia tăng sức ép hoặc củng cố vị trí quân tốt hơn.`;
    }
  }

  let correctStrategy: string | undefined;
  if (bestAltVi) {
    if (['chariot', 'horse', 'cannon'].includes(pieceKind)) {
      correctStrategy = `Nên đi ${bestAltVi}: Xuất quân chủ lực chiếm lộ thông thoáng (lộ 4, 6) hoặc khống chế trung lộ trước khi vội vã tấn công.`;
    } else if (['advisor', 'elephant', 'king'].includes(pieceKind) || isCheck) {
      correctStrategy = `Nên đi ${bestAltVi}: Củng cố thế trận phòng thủ kín kẽ, giữ chắc cặp Sĩ Tượng để bảo vệ Tướng an toàn.`;
    } else {
      correctStrategy = `Nên đi ${bestAltVi}: Duy trì cấu trúc đội hình vững chắc, giữ thế chủ động và không để đối phương phản tiên.`;
    }
  }

  let aiCommentary = 'Nước đi tự nhiên, duy trì thế trận ổn định.';
  if (isCheck && (badge === 'blunder' || badge === 'mistake')) {
    aiCommentary = `Đỡ chiếu sai lầm! ${sideName} đi ${notationVi} làm rạn nứt hệ thống phòng thủ, dẫn đến tổn thất lớn.`;
  } else if (badge === 'brilliant') {
    aiCommentary = `Nước đi thiên tài! ${sideName} tung đòn phối hợp bất ngờ mang tính quyết định, tạo ưu thế áp đảo.`;
  } else if (badge === 'blunder') {
    aiCommentary = `Sai lầm nghiêm trọng (Blunder)! Nước ${notationVi} làm mất quyền kiểm soát thế trận hoặc thiệt hại quân nặng nề.`;
  } else if (badge === 'mistake') {
    aiCommentary = `Nước cờ lỗi (Mistake)! ${sideName} để lộ sơ hở khiến đối phương chớp thời cơ phản công chiếm ưu thế.`;
  } else if (badge === 'inaccuracy') {
    aiCommentary = `Nước cờ chưa sắc bén. Có phương án tối ưu hơn để duy trì nhịp độ tấn công.`;
  } else if (badge === 'best') {
    aiCommentary = `Nước đi tối ưu nhất theo đánh giá của máy! Kiểm soát hoàn hảo thế trận.`;
  } else if (badge === 'excellent') {
    aiCommentary = `Nước cờ rất chuẩn xác, giữ vững cấu trúc đội hình vững chắc.`;
  } else if (revealedKind) {
    const vName = KIND_VI_NAME[revealedKind as PieceKind] || revealedKind;
    aiCommentary = `Mở nắp lật ra ${vName}! ${['chariot', 'cannon', 'horse'].includes(revealedKind) ? 'Gia tăng sức ép chiến thuật ngay lập tức.' : 'Giữ vững nhịp độ trận đấu.'}`;
  } else if (capturedPiece) {
    aiCommentary = `Ăn quân dứt khoát (${capturedPiece}), củng cố ưu thế lực lượng.`;
  } else if (isCheck) {
    aiCommentary = 'Nước chiếu công kích trực diện, buộc đối phương phải tìm cách chống đỡ.';
  }

  return {
    aiCommentary,
    strategicTag,
    ...(whyWrong ? { whyWrong } : {}),
    ...(correctStrategy ? { correctStrategy } : {}),
  };
}

/**
 * Xây dựng báo cáo Game Review từ dữ liệu Replay
 */
export function buildGameReviewFromReplay(
  replayData: MatchReplayData,
  _core?: XiangqiCore | null,
): GameReviewReport {
  // Fast, instant client-side review (< 2ms) without blocking the browser thread
  const totalPlies = replayData.moves.length;
  const moves: MoveReview[] = [];
  const evalTrajectory: EvalPoint[] = [{ ply: 0, evalCp: 0, winRateRed: 50 }];

  const badgeCountsRed: Record<MoveBadge, number> = {
    brilliant: 0, best: 0, excellent: 0, good: 0, inaccuracy: 0, mistake: 0, blunder: 0, forced: 0,
  };
  const badgeCountsBlack: Record<MoveBadge, number> = {
    brilliant: 0, best: 0, excellent: 0, good: 0, inaccuracy: 0, mistake: 0, blunder: 0, forced: 0,
  };

  const redAccuracies: number[] = [];
  const blackAccuracies: number[] = [];

  interface PieceState {
    side: Side;
    kind: PieceKind;
    revealed: boolean;
    initialRole: PieceKind;
  }
  const board: (PieceState | null)[] = new Array(90).fill(null);
  const isBlind = replayData.ruleset !== 'standard';

  for (let f = 0; f < 9; f++) {
    const s0 = sq(0, f);
    const r0 = getInitialSquareRole(s0);
    board[s0] = { side: 'red', kind: r0, revealed: !isBlind || r0 === 'king', initialRole: r0 };
  }
  board[sq(2, 1)] = { side: 'red', kind: 'cannon', revealed: !isBlind, initialRole: 'cannon' };
  board[sq(2, 7)] = { side: 'red', kind: 'cannon', revealed: !isBlind, initialRole: 'cannon' };
  for (let f = 0; f < 9; f += 2) {
    board[sq(3, f)] = { side: 'red', kind: 'soldier', revealed: !isBlind, initialRole: 'soldier' };
  }

  for (let f = 0; f < 9; f++) {
    const s9 = sq(9, f);
    const r9 = getInitialSquareRole(s9);
    board[s9] = { side: 'black', kind: r9, revealed: !isBlind || r9 === 'king', initialRole: r9 };
  }
  board[sq(7, 1)] = { side: 'black', kind: 'cannon', revealed: !isBlind, initialRole: 'cannon' };
  board[sq(7, 7)] = { side: 'black', kind: 'cannon', revealed: !isBlind, initialRole: 'cannon' };
  for (let f = 0; f < 9; f += 2) {
    board[sq(6, f)] = { side: 'black', kind: 'soldier', revealed: !isBlind, initialRole: 'soldier' };
  }

  let currentRedAdvantageCp = 0;
  const valMap: Record<PieceKind, number> = {
    chariot: 9000, cannon: 4500, horse: 4000, elephant: 2000, advisor: 2000, soldier: 1000, king: 100000,
  };

  let biggestBlunderRed: KeyBlunderInfo | undefined;
  let biggestBlunderBlack: KeyBlunderInfo | undefined;
  let maxLossRed = 0;
  let maxLossBlack = 0;

  for (let i = 0; i < replayData.moves.length; i++) {
    const m = replayData.moves[i];
    if (!m) continue;
    const ply = m.ply ?? i + 1;
    const side: Side = i % 2 === 0 ? 'red' : 'black';
    const moveNumber = Math.floor(i / 2) + 1;

    const movingPiece = board[m.from];
    const targetPiece = board[m.to];
    const isCapture = !!targetPiece;
    const capturedName = targetPiece ? KIND_VI_NAME[targetPiece.kind] : undefined;
    const isCheck = !!m.check;
    const revealedPieceKind = m.revealed?.kind;

    const prevRedAdvantage = currentRedAdvantageCp;

    if (isCapture && targetPiece) {
      const gain = (valMap[targetPiece.kind] || 2000) * (side === 'red' ? 1 : -1);
      currentRedAdvantageCp += gain;
    }
    if (revealedPieceKind) {
      const revKind = revealedPieceKind as PieceKind;
      const revBonus = (valMap[revKind] || 2000) * 0.25 * (side === 'red' ? 1 : -1);
      currentRedAdvantageCp += revBonus;
    }

    const evalForPlayer = side === 'red' ? currentRedAdvantageCp : -currentRedAdvantageCp;
    const prevEvalForPlayer = side === 'red' ? prevRedAdvantage : -prevRedAdvantage;
    const evalDelta = evalForPlayer - prevEvalForPlayer;

    const winRate = cpToWinRate(evalForPlayer) * 100;
    const prevWinRate = cpToWinRate(prevEvalForPlayer) * 100;
    const winRateDrop = Math.max(0, (prevWinRate - winRate) / 100);

    const moveAcc = calculateMoveAccuracy(winRateDrop);
    if (side === 'red') redAccuracies.push(moveAcc);
    else blackAccuracies.push(moveAcc);

    const fromCol = getFileCol(m.from, side);
    const toCol = getFileCol(m.to, side);
    const fromR = getRank(m.from);
    const toR = getRank(m.to);
    const rDiff = Math.abs(toR - fromR);

    const pieceKind: PieceKind = movingPiece ? (movingPiece.revealed ? movingPiece.kind : movingPiece.initialRole) : 'soldier';
    const pieceAbbr = KIND_VI_ABBR[pieceKind] || 'Q';
    const piecePrefix = movingPiece && !movingPiece.revealed ? `(${pieceAbbr})` : pieceAbbr;

    let moveAction = '-';
    let moveSuffix = `${toCol}`;
    if (fromCol === toCol) {
      moveAction = (side === 'red' && toR > fromR) || (side === 'black' && toR < fromR) ? '.' : '/';
      moveSuffix = `${rDiff}`;
    } else {
      if ((side === 'red' && toR > fromR) || (side === 'black' && toR < fromR)) moveAction = '.';
      else if ((side === 'red' && toR < fromR) || (side === 'black' && toR > fromR)) moveAction = '/';
      else moveAction = '-';
    }

    let notationVi = `${piecePrefix}${fromCol}${moveAction}${moveSuffix}`;
    if (revealedPieceKind) notationVi += ` -> ${KIND_VI_ABBR[revealedPieceKind as PieceKind] || revealedPieceKind}`;

    const badge = classifyMoveBadge(winRateDrop, evalDelta, false, evalDelta >= 2500);

    if (side === 'red') badgeCountsRed[badge] = (badgeCountsRed[badge] ?? 0) + 1;
    else badgeCountsBlack[badge] = (badgeCountsBlack[badge] ?? 0) + 1;

    const strategy = generateStrategicDetails(
      badge,
      side,
      notationVi,
      pieceKind,
      evalDelta,
      revealedPieceKind,
      capturedName,
      isCheck,
    );

    if (badge === 'blunder' || badge === 'mistake') {
      const blunderCandidate: KeyBlunderInfo = {
        ply,
        moveNumber,
        side,
        notationVi,
        evalDeltaCp: Math.round(evalDelta),
        winRateLoss: Math.round(winRateDrop * 1000) / 10,
        description: `Nước ${moveNumber} (${notationVi}): Làm tụt ${Math.round(winRateDrop * 100)}% xác suất thắng.`,
        whyWrong: strategy.whyWrong,
        correctStrategy: strategy.correctStrategy,
      };
      if (side === 'red' && winRateDrop > maxLossRed) {
        maxLossRed = winRateDrop;
        biggestBlunderRed = blunderCandidate;
      } else if (side === 'black' && winRateDrop > maxLossBlack) {
        maxLossBlack = winRateDrop;
        biggestBlunderBlack = blunderCandidate;
      }
    }

    if (movingPiece) {
      board[m.from] = null;
      movingPiece.revealed = true;
      if (revealedPieceKind) movingPiece.kind = revealedPieceKind as PieceKind;
      board[m.to] = movingPiece;
    }

    evalTrajectory.push({
      ply,
      evalCp: Math.round(currentRedAdvantageCp),
      winRateRed: Math.round(cpToWinRate(currentRedAdvantageCp) * 1000) / 10,
    });

    moves.push({
      ply,
      moveNumber,
      side,
      from: m.from,
      to: m.to,
      notationVi,
      notationIccs: m.notation || '',
      evalCp: Math.round(evalForPlayer),
      prevEvalCp: Math.round(prevEvalForPlayer),
      evalDeltaCp: Math.round(evalDelta),
      winRate: Math.round(winRate * 10) / 10,
      winRateDelta: Math.round(-winRateDrop * 1000) / 10,
      moveAccuracy: moveAcc,
      badge,
      aiCommentary: strategy.aiCommentary,
      strategicTag: strategy.strategicTag,
      whyWrong: strategy.whyWrong,
      correctStrategy: strategy.correctStrategy,
      advantageDescription: describeAdvantage(evalForPlayer),
      isCheck,
      isCapture,
      ...(capturedName ? { capturedPiece: capturedName } : {}),
      ...(revealedPieceKind ? { revealedPiece: revealedPieceKind } : {}),
    });
  }

  const redMeanAcc = redAccuracies.length > 0 ? Math.round((redAccuracies.reduce((a, b) => a + b, 0) / redAccuracies.length) * 10) / 10 : 80;
  const blackMeanAcc = blackAccuracies.length > 0 ? Math.round((blackAccuracies.reduce((a, b) => a + b, 0) / blackAccuracies.length) * 10) / 10 : 80;

  const redWeaknesses: string[] = [];
  if (biggestBlunderRed) {
    redWeaknesses.push(
      `Nước ${biggestBlunderRed.moveNumber} (${biggestBlunderRed.notationVi}): ${biggestBlunderRed.whyWrong || 'Sơ hở lớn nhất ván đấu'}`,
    );
  }

  const blackWeaknesses: string[] = [];
  if (biggestBlunderBlack) {
    blackWeaknesses.push(
      `Nước ${biggestBlunderBlack.moveNumber} (${biggestBlunderBlack.notationVi}): ${biggestBlunderBlack.whyWrong || 'Sơ hở lớn nhất ván đấu'}`,
    );
  }

  let criticalTurningPoint: KeyBlunderInfo | undefined;
  if (biggestBlunderRed && biggestBlunderBlack) {
    criticalTurningPoint = maxLossRed >= maxLossBlack ? biggestBlunderRed : biggestBlunderBlack;
  } else {
    criticalTurningPoint = biggestBlunderRed || biggestBlunderBlack;
  }

  const redPerformance: PlayerPerformance = {
    side: 'red',
    playerName: replayData.players.red.display_name || 'Bên Đỏ',
    accuracy: redMeanAcc,
    ratingEstimate: estimateRating(redMeanAcc),
    badgeCounts: badgeCountsRed,
    tacticalStrengths: ['Khai cuộc chủ động'],
    tacticalWeaknesses: redWeaknesses,
    biggestBlunder: biggestBlunderRed,
  };

  const blackPerformance: PlayerPerformance = {
    side: 'black',
    playerName: replayData.players.black.display_name || 'Bên Đen',
    accuracy: blackMeanAcc,
    ratingEstimate: estimateRating(blackMeanAcc),
    badgeCounts: badgeCountsBlack,
    tacticalStrengths: ['Phòng ngự chặt chẽ'],
    tacticalWeaknesses: blackWeaknesses,
    biggestBlunder: biggestBlunderBlack,
  };

  const winnerStr = replayData.result?.winner === 'red' ? 'Bên Đỏ' : replayData.result?.winner === 'black' ? 'Bên Đen' : 'Hòa';
  let executiveSummary =
    `Ván đấu kết thúc sau ${replayData.moves.length} nước với chiến thắng thuộc về ${winnerStr}. ` +
    `Bên Đỏ đạt độ chính xác ${redMeanAcc}% (~${redPerformance.ratingEstimate} Elo), ` +
    `Bên Đen đạt độ chính xác ${blackMeanAcc}% (~${blackPerformance.ratingEstimate} Elo). `;

  if (criticalTurningPoint) {
    executiveSummary += `Bước ngoặt quyết định diễn ra ở nước ${criticalTurningPoint.moveNumber} (${criticalTurningPoint.side === 'red' ? 'Đỏ' : 'Đen'} đi ${criticalTurningPoint.notationVi}).`;
  }

  return {
    matchId: replayData.match_id,
    ruleset: replayData.ruleset,
    winner: replayData.result?.winner ?? 'draw',
    totalPlies,
    totalMoves: Math.ceil(totalPlies / 2),
    redPerformance,
    blackPerformance,
    evalTrajectory,
    moves,
    executiveSummary,
    criticalTurningPoint,
    ...(replayData.started_at ? { date: new Date(replayData.started_at).toLocaleString('vi-VN') } : {}),
    ...(replayData.result?.reason ? { winReason: replayData.result.reason } : {}),
  };
}
